import { MERMAID_CLASS } from '@markroom/renderer';

export const MARK_CLASS = 'mr-find';
export const CURRENT_CLASS = 'mr-find--current';
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'MARK']);

/** Remove all find highlights, restoring original text. */
export function clearMarks(root: HTMLElement | null): void {
  if (!root) return;
  const marks = root.querySelectorAll<HTMLElement>(`mark.${MARK_CLASS}`);
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    parent.replaceChild(document.createTextNode(mark.textContent ?? ''), mark);
    parent.normalize();
  });
}

/** Wrap every case-insensitive occurrence of `query` in a <mark>; return them in order. */
export function highlight(root: HTMLElement, query: string): HTMLElement[] {
  const needle = query.toLowerCase();
  const marks: HTMLElement[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
      if (parent.closest(`.${MERMAID_CLASS}`)) return NodeFilter.FILTER_REJECT;
      if (!node.nodeValue || !node.nodeValue.toLowerCase().includes(needle)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const targets: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) targets.push(n as Text);

  for (const textNode of targets) {
    const value = textNode.nodeValue ?? '';
    const lower = value.toLowerCase();
    const frag = document.createDocumentFragment();
    let pos = 0;
    for (;;) {
      const idx = lower.indexOf(needle, pos);
      if (idx === -1) break;
      if (idx > pos) frag.appendChild(document.createTextNode(value.slice(pos, idx)));
      const mark = document.createElement('mark');
      mark.className = MARK_CLASS;
      mark.textContent = value.slice(idx, idx + needle.length);
      frag.appendChild(mark);
      marks.push(mark);
      pos = idx + needle.length;
    }
    if (pos < value.length) frag.appendChild(document.createTextNode(value.slice(pos)));
    textNode.parentNode?.replaceChild(frag, textNode);
  }
  return marks;
}
